import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { 
  Brain, Heart, Users, Sparkles, CheckCircle, XCircle,
  Eye, Ear, Hand, MessageSquare, BookOpen, ArrowRight, ChevronDown
} from 'lucide-react';

// Infinity Symbol Component
const InfinitySymbol = ({ className = "h-12 w-12" }) => (
  <svg viewBox="0 0 100 50" className={className}>
    <defs>
      <linearGradient id="infinityGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#e52b5b" />
        <stop offset="25%" stopColor="#ff8c00" />
        <stop offset="50%" stopColor="#ffd700" />
        <stop offset="75%" stopColor="#32cd32" />
        <stop offset="100%" stopColor="#4169e1" />
      </linearGradient>
    </defs>
    <path
      d="M25,25 C25,10 10,10 10,25 C10,40 25,40 25,25 C25,10 75,10 75,25 C75,40 90,40 90,25 C90,10 75,10 75,25"
      fill="none"
      stroke="url(#infinityGradient)"
      strokeWidth="6"
      strokeLinecap="round"
    />
  </svg>
);

const mythsVsFacts = [
  {
    myth: "Autism is caused by bad parenting",
    fact: "Autism is a neurodevelopmental difference with genetic and environmental factors. Parenting has nothing to do with causing autism."
  },
  {
    myth: "All autistic people are the same",
    fact: "Autism is a spectrum. Every autistic person is unique with their own strengths, challenges, and personality."
  },
  {
    myth: "Autistic people do not want friends or relationships",
    fact: "Many autistic people desire connection and relationships, though they may express this differently."
  },
  {
    myth: "Autism needs to be cured",
    fact: "Autism is a natural form of human neurodiversity. Many autistic people do not want to be cured. They want acceptance and support."
  },
  {
    myth: "Autistic people lack empathy",
    fact: "Autistic people often experience deep empathy, though they may express or process it differently."
  },
  {
    myth: "You can grow out of autism",
    fact: "Autism is a lifelong neurotype. While skills can develop and support needs may change, a person remains autistic throughout their life."
  }
];

const sensoryDifferences = [
  { sense: 'Sight', icon: Eye, examples: 'Sensitivity to bright lights, fluorescent lighting, or visual clutter' },
  { sense: 'Sound', icon: Ear, examples: 'Overwhelm from loud noises, background noise, or certain frequencies' },
  { sense: 'Touch', icon: Hand, examples: 'Discomfort with certain textures, clothing tags, or light touch' },
  { sense: 'Taste and Smell', icon: Sparkles, examples: 'Strong preferences or aversions to foods, smells, or flavours' }
];

const strengths = [
  'Attention to detail and pattern recognition',
  'Deep focus and expertise in areas of interest',
  'Honest and direct communication',
  'Logical and systematic thinking',
  'Strong memory for facts and information',
  'Unique perspectives and creative problem solving',
  'Loyalty and dedication in relationships',
  'Authenticity and genuineness'
];

export default function Learn() {
  const location = useLocation();
  
  // Handle hash navigation for sections
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.replace('#', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  const scrollToContent = () => {
    const element = document.getElementById('what-is-autism');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[hsl(195,77%,95%)] via-background to-[hsl(166,56%,95%)] py-12 md:py-16">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-4">
              <img src="/infinity.png" alt="Neurodiversity Infinity Symbol" className="h-16 w-auto" />
            </div>
            <Badge className="mb-4 bg-[hsl(195,77%,90%)] text-[hsl(195,77%,35%)]">Education & Information</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Understanding Autism
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Learn about autism from a neurodiversity perspective. One that celebrates differences, 
              promotes acceptance, and recognises autistic individuals as whole, complete people.
            </p>
            {/* Scroll Down Arrow */}
            <button 
              onClick={scrollToContent}
              className="flex flex-col items-center gap-2 text-muted-foreground hover:text-primary transition-colors animate-bounce mx-auto"
            >
              <span className="text-sm">Scroll to learn more</span>
              <ChevronDown className="h-6 w-6" />
            </button>
          </div>
        </div>
      </section>

      {/* What is Autism */}
      <section id="what-is-autism" className="py-12 scroll-mt-20">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold mb-6">What is Autism?</h2>
              <p className="text-muted-foreground mb-4">
                Autism is a natural neurological variation that affects how a person perceives, 
                interacts with, and experiences the world. It is not a disease or disorder that needs to be cured.
              </p>
              <p className="text-muted-foreground mb-4">
                From a neurodiversity perspective, autism is understood as one of many natural 
                variations in human neurology, like having different eye colors or being 
                left-handed. Autistic brains are simply wired differently, not wrongly.
              </p>
              <p className="text-muted-foreground">
                Autism affects communication, social interaction, sensory processing, and how 
                a person thinks and learns. The experience of being autistic varies greatly 
                from person to person, hence the term "spectrum."
              </p>
            </div>
            <Card className="card-base bg-[hsl(195,77%,95%)]">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <InfinitySymbol className="h-10 w-10" />
                  The Infinity Symbol
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  The rainbow infinity symbol represents autism and neurodiversity in the 
                  acceptance movement. It symbolises the infinite diversity of autistic 
                  experiences and the natural, continuous spectrum of human neurology.
                </p>
                <p className="text-sm text-muted-foreground italic">
                  Note: We do not use puzzle piece imagery as it is associated with outdated 
                  and harmful narratives about autism.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Myths vs Facts - Moved Up */}
      <section id="myths" className="py-12 bg-muted/30 scroll-mt-20">
        <div className="container-main">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-[hsl(346,100%,90%)] text-[hsl(346,100%,45%)]">Debunking Misconceptions</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Myths vs Facts</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              There are many misconceptions about autism. Let us separate fact from fiction.
            </p>
          </div>
          <div className="grid gap-4">
            {mythsVsFacts.map((item, i) => (
              <Card key={i} className="card-base">
                <CardContent className="p-5">
                  <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                          <XCircle className="h-5 w-5 text-red-600" />
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-red-600 mb-1">Myth</p>
                        <p className="text-muted-foreground">{item.myth}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-[hsl(166,56%,90%)] flex items-center justify-center">
                          <CheckCircle className="h-5 w-5 text-[hsl(166,56%,45%)]" />
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-[hsl(166,56%,45%)] mb-1">Fact</p>
                        <p className="text-muted-foreground">{item.fact}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sensory Differences */}
      <section id="sensory" className="py-12 scroll-mt-20">
        <div className="container-main">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-[hsl(27,100%,90%)] text-[hsl(27,100%,40%)]">Understanding Sensory Processing</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Sensory Differences</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Many autistic people experience sensory input more intensely or differently 
              than neurotypical people. This can lead to both sensory seeking and sensory avoidance.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {sensoryDifferences.map((sense) => (
              <Card key={sense.sense} className="card-base">
                <CardHeader className="pb-2">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(166,56%,90%)] flex items-center justify-center mb-2">
                    <sense.icon className="h-6 w-6 text-[hsl(166,56%,45%)]" />
                  </div>
                  <CardTitle className="text-xl">{sense.sense}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{sense.examples}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Strengths */}
      <section id="strengths" className="py-12 bg-[hsl(195,77%,45%)] text-white scroll-mt-20">
        <div className="container-main">
          <div className="text-center mb-10">
            <Badge className="bg-[hsl(346,100%,65%)] text-white border-none mb-4">Neurodiversity Strengths</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Autistic Strengths</h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              Autistic individuals bring unique strengths and perspectives to the world.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {strengths.map((strength, i) => (
              <div key={i} className="bg-white/10 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[hsl(346,100%,65%)] flex-shrink-0 mt-0.5" />
                <span>{strength}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Communication Styles */}
      <section id="communication" className="py-12 scroll-mt-20">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <Badge className="mb-4 bg-[hsl(54,100%,90%)] text-[hsl(54,100%,30%)]">Communication</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Communication Differences</h2>
            </div>
            <Card className="card-base">
              <CardContent className="p-6 md:p-8 space-y-5">
                <div>
                  <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-[hsl(195,77%,45%)]" />
                    Direct Communication
                  </h3>
                  <p className="text-muted-foreground">
                    Many autistic people communicate directly and literally. They may not pick up 
                    on or use subtle hints, sarcasm, or implied meanings. This directness is not 
                    rudeness. It is a different communication style.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-[hsl(195,77%,45%)]" />
                    Processing Time
                  </h3>
                  <p className="text-muted-foreground">
                    Autistic individuals may need more time to process verbal information and 
                    formulate responses. Patience and allowing processing time supports better 
                    communication.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-[hsl(195,77%,45%)]" />
                    Alternative Communication
                  </h3>
                  <p className="text-muted-foreground">
                    Some autistic people use alternative communication methods like typing, 
                    AAC devices, sign language, or written communication. All forms of 
                    communication are valid.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 bg-muted/30">
        <div className="container-main text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Learn More?</h2>
          <p className="text-muted-foreground mb-6">
            Explore our resource library for guides, tools, and more information.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/resources">
              <Button className="btn-primary">
                <BookOpen className="mr-2 h-4 w-4" />
                Browse Resources
              </Button>
            </Link>
            <Link to="/directory">
              <Button variant="outline">
                Find Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
